// @ts-nocheck
import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "@tanstack/react-router";
import { trpcClient } from "@/lib/trpc";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, AlertCircle, Wrench, Activity, ImageIcon, History } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RelatedDataTabs } from "@/components/shared/RelatedDataTabs";
import { EntityLink } from "@/components/shared/EntityLink";
import DetailLoadingSkeleton from "@/components/shared/DetailLoadingSkeleton";
import ImageLightbox from "@/components/shared/ImageLightbox";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { formatDate, formatDateTime, getUploadUrl } from "@/lib/utils";
import { DetailField as F } from "@/components/shared/DetailField";

const LOG_TYPE: Record<string, string> = { OPERATION: "Vận hành", DOWNTIME: "Sự cố / dừng máy", MAINTENANCE: "Bảo trì", SETUP: "Căn chỉnh" };

const MACHINE_STATUS: Record<string, { label: string; color: string }> = {
  IDLE: {
    label: "Sẵn sàng",
    color: "bg-slate-50 text-slate-600 border-slate-200",
  },
  RUNNING: {
    label: "Đang chạy",
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  MAINTENANCE: {
    label: "Bảo trì",
    color: "bg-amber-50 text-amber-700 border-amber-200",
  },
  BROKEN: {
    label: "Sự cố",
    color: "bg-rose-50 text-rose-700 border-rose-200",
  },
  INACTIVE: {
    label: "Ngừng h/động",
    color: "bg-slate-100 text-slate-400 border-slate-200",
  },
};

/** Ô thông tin — cùng kiểu trang chi tiết Sản phẩm (nhãn trái w-24, giá trị đậm; trống hiện —) */

const MACHINE_TYPE_LABEL: Record<string, string> = {
  OFFSET_PRINTER: "Máy in Offset",
  FLEXO_PRINTER: "Máy in Flexo",
  PAPER_CUTTER: "Máy xén giấy",
  COATER: "Máy tráng phủ",
  SILK_SCREEN: "Máy in lụa",
  LAMINATOR: "Máy cán màng",
  HOT_FOIL: "Máy ép kim",
  DIE_CUTTER: "Máy bế",
  WINDOW_PATCHER: "Máy dán cửa sổ",
  FOLDER_GLUER: "Máy gấp dán",
  PACKING: "Máy đóng gói",
  OTHER: "Khác",
};

const STEP_STATUS: Record<string, { label: string; cls: string }> = {
  PLANNED: { label: "Chờ xếp", cls: "bg-slate-50 text-slate-600 border-slate-200" },
  SCHEDULED: { label: "Đã xếp lịch", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  READY: { label: "Đã ban hành", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  RUNNING: { label: "Đang chạy", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  PAUSED: { label: "Tạm dừng", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

const fmtNum = (n: unknown) => (n === null || n === undefined || n === "" ? null : Number(n).toLocaleString("vi-VN"));

/** Bảng gọn dùng chung cho các tab của máy */
function MiniTable({ head, rows, empty }: { head: string[]; rows: React.ReactNode[][]; empty: string }) {
  if (!rows.length) return <div className="text-center text-sm text-slate-500 py-8">{empty}</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-600">
          <tr>
            {head.map((h) => (
              <th key={h} className="h-9 px-3 text-left font-semibold whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
              {r.map((c, k) => (
                <td key={k} className="px-3 py-2 align-top">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MachineView({ machineId: propsId }: { machineId?: string }) {
  const params = useParams({ strict: false });
  // Route /machines/$machineId truyền param tên `machineId` (trước đây đọc nhầm `id` nên trang trắng)
  const machineId = propsId || (params as any).machineId || (params as any).id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showDelete, setShowDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: d, isLoading, isError } = useQuery({
    queryKey: ["machine", machineId],
    queryFn: () => trpcClient.machines.getById.query({ id: machineId }),
    enabled: !!machineId,
  });

  const machineKey = d?.id;
  const { data: stepsData } = useQuery({
    queryKey: ["machine-steps", machineKey],
    queryFn: () =>
      trpcClient.productionPlanning.getStageWorkOrders.query({
        machineId: machineKey,
        status: ["PLANNED", "SCHEDULED", "READY", "RUNNING", "PAUSED"],
        poStatusFilter: "ALL",
        sortBy: "plannedStartDate",
        sortOrder: "asc",
        limit: 100,
      } as any),
    enabled: !!machineKey,
  });
  const { data: logsData } = useQuery({
    queryKey: ["machine-logs-of", machineKey],
    queryFn: () => trpcClient.machineLogs.list.query({ machineId: machineKey, limit: 100, sortBy: "date", sortOrder: "desc" } as any),
    enabled: !!machineKey,
  });

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await trpcClient.machines.delete.mutate({ id: machineId });
      await queryClient.invalidateQueries({ queryKey: ["machines"] });
      toast.success("Xoá thiết bị thành công");
      setShowDelete(false);
      navigate({ to: "/machines" });
    } catch (e: any) {
      toast.error("Không thể xoá thiết bị: " + e.message);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <DetailLoadingSkeleton breadcrumbs={[{ label: "Thiết bị", to: "/machines" }]} />;
  }

  if (!d) {
    // Không để trang trắng: hiển thị trạng thái không tìm thấy / lỗi tải
    return (
      <StandardDetailLayout breadcrumbs={[{ label: "Thiết bị", to: "/machines" }]} title="Không tìm thấy thiết bị" onBack={() => navigate({ to: "/machines" })}>
        <div className="flex flex-col items-center justify-center gap-3 p-12 text-slate-500">
          <AlertCircle className="w-10 h-10 text-slate-300" />
          <p className="font-medium text-slate-600">
            {isError ? "Không thể tải thông tin thiết bị." : `Thiết bị "${machineId || ""}" không tồn tại hoặc đã bị xóa.`}
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate({ to: "/machines" })}>
            Quay lại danh sách
          </Button>
        </div>
      </StandardDetailLayout>
    );
  }

  const sts = MACHINE_STATUS[d.status || "IDLE"] || MACHINE_STATUS.IDLE;

  let specsObj: Record<string, any> = {};
  try {
    if (d.technicalSpecs) specsObj = typeof d.technicalSpecs === "string" ? JSON.parse(d.technicalSpecs) : d.technicalSpecs;
    else if (d.specs) specsObj = typeof d.specs === "string" ? JSON.parse(d.specs) : d.specs;
  } catch {}

  const steps: any[] = (stepsData as any)?.items || [];
  const logs: any[] = (logsData as any)?.items || [];

  return (
    <>
      <StandardDetailLayout
        breadcrumbs={[{ label: "Thiết bị", to: "/machines" }]}
        title={d.name}
        entityId={d.id}
        entityType="machine"
        status={<Badge variant="outline" className={sts.color}>{sts.label}</Badge>}
        actions={
          <div className="flex gap-1.5">
            <Button
              size="sm"
              data-testid="btn-edit-machine"
              onClick={() => navigate({ to: `/machines/${d.machineId || machineId}/edit` })}
            >
              <Edit size={14} className="mr-1.5" /> Sửa
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="btn-delete-machine"
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
              onClick={() => setShowDelete(true)}
            >
              <Trash2 size={14} className="mr-1.5" /> Xoá
            </Button>
          </div>
        }
        onBack={() => navigate({ to: "/machines" })}
      >
        {/* ═══ 1. THÔNG TIN (phẳng, như trang Sản phẩm): 3 cột thông tin + ảnh máy ═══ */}
        <div className="border border-slate-200 rounded-lg px-4 py-4 bg-white" data-testid="machine-info-card">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-5 items-start">
            <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
              <F label="Mã máy" value={d.machineId} mono hl />
              <F label="Loại máy" value={MACHINE_TYPE_LABEL[d.type] || d.type} />
              <F label="Nhóm công đoạn" value={d.processCategory} />
              <F label="Tổ sản xuất" value={d.department?.name} />
              <F label="Hãng / Quốc gia" value={[d.manufacturer, d.countryOfOrigin].filter(Boolean).join(" · ")} />
              <F label="Model" value={d.technicalModel} />
              <F label="Năm SX" value={d.yearManufactured} mono />
              <F label="Số serial" value={d.serialNumber} mono />
              <F label="Công suất" value={d.powerConsumptionKw ? `${d.powerConsumptionKw} kW` : null} />
              <F label="Khổ làm việc" value={d.workingFormat} hl />
              <F label="Khổ kẽm CTP" value={d.plateSize} hl />
              <F label="Định lượng giấy" value={d.paperWeightRange} />
              <F label="Sản lượng chuẩn" value={d.standardProductivity ? `${fmtNum(d.standardProductivity)} tờ/giờ` : null} />
              <F label="T/g setup chuẩn" value={d.setupTimeMinutes ? `${d.setupTimeMinutes} phút` : null} />
              <F label="Tổng giờ chạy" value={`${fmtNum(d.totalRunningHours || 0)} giờ`} />
              <F label="Tổng sản lượng" value={fmtNum(d.totalOutputQuantity || 0)} />
              <F
                label="Chu kỳ bảo trì"
                value={[d.maintenanceCycleHours ? `${d.maintenanceCycleHours} giờ chạy` : null, d.maintenanceCycleDays ? `${d.maintenanceCycleDays} ngày` : null]
                  .filter(Boolean)
                  .join(" · ")}
              />
              <F
                label="Bảo trì kế tiếp"
                value={[d.nextMaintenanceDate ? formatDate(d.nextMaintenanceDate) : null, d.nextMaintenanceHours ? `còn ${d.nextMaintenanceHours} giờ` : null]
                  .filter(Boolean)
                  .join(" · ")}
                hl
              />
            </div>

            {/* Ảnh máy */}
            <div className="lg:col-span-1">
              {d.imageUrl ? (
                <ImageLightbox src={getUploadUrl(d.imageUrl)} alt={d.name}>
                  <div className="border border-slate-200 rounded-lg overflow-hidden bg-white p-1 shadow-sm cursor-zoom-in hover:border-indigo-300">
                    <img src={getUploadUrl(d.imageUrl)} alt={d.name} className="object-cover w-full h-auto max-h-[220px] rounded-md" />
                  </div>
                </ImageLightbox>
              ) : (
                <div className="border border-slate-200 rounded-lg bg-slate-50 flex items-center justify-center p-6 flex-col gap-2 min-h-[160px]">
                  <ImageIcon className="h-6 w-6 text-slate-300" />
                  <span className="text-xs text-slate-600 font-medium">Chưa có hình ảnh</span>
                </div>
              )}
            </div>
          </div>

          {/* Công đoạn máy làm được + thông số mở rộng */}
          <div className="mt-4 pt-3 border-t border-slate-100 flex flex-col gap-3">
            <div className="flex gap-3 items-baseline min-w-0">
              <span className="text-slate-700 text-[11px] shrink-0 w-24">Công đoạn</span>
              <div className="flex flex-wrap gap-1.5 flex-1">
                {(d.processes || []).length > 0 ? (
                  d.processes.map((mp: any) => (
                    <EntityLink key={mp.process?.id || mp.processId} entityType="process" id={mp.process?.id} label={mp.process?.name} code={mp.process?.stepId} />
                  ))
                ) : (
                  <span className="text-slate-300 text-sm">—</span>
                )}
              </div>
            </div>
            {Object.keys(specsObj).length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2">
                {Object.entries(specsObj).map(([k, v]) => (
                  <F key={k} label={k} value={typeof v === "object" ? JSON.stringify(v) : String(v)} mono />
                ))}
              </div>
            )}
            {d.notes && <F label="Ghi chú" value={<span className="whitespace-pre-wrap font-medium">{d.notes}</span>} />}
          </div>
        </div>

        {/* ═══ 2. DỮ LIỆU LIÊN QUAN (dữ liệu thật, không để mẫu cố định) ═══ */}
        <RelatedDataTabs
          defaultTab="queue"
          tabs={[
            {
              id: "queue",
              label: "Lịch chạy",
              icon: <Activity className="h-4 w-4" />,
              count: steps.length,
              content: (
                <MiniTable
                  head={["Lệnh SX", "Công đoạn", "Sản phẩm", "Bắt đầu", "Kết thúc", "Hạn giao", "Trạng thái"]}
                  empty="Máy chưa có công đoạn nào đang chờ hoặc đang chạy."
                  rows={steps.map((st: any) => [
                    <span className="whitespace-nowrap"><EntityLink entityType="productionOrder" id={st.productionOrderId} code={st.poCode} /></span>,
                    st.name,
                    <span className="text-slate-700">{st.productName || "—"}</span>,
                    <span className="tabular-nums whitespace-nowrap">{st.plannedStartDate ? formatDateTime(st.plannedStartDate) : "—"}</span>,
                    <span className={`tabular-nums whitespace-nowrap ${st.isOverdue ? "text-rose-600 font-semibold" : ""}`}>
                      {st.plannedEndDate ? formatDateTime(st.plannedEndDate) : "—"}
                    </span>,
                    <span className="tabular-nums whitespace-nowrap">{st.deadline ? formatDate(st.deadline) : "—"}</span>,
                    <Badge variant="outline" className={`text-[10px] ${(STEP_STATUS[st.status] || STEP_STATUS.PLANNED).cls}`}>
                      {(STEP_STATUS[st.status] || { label: st.status }).label}
                    </Badge>,
                  ])}
                />
              ),
            },
            ...(
              [
                { id: "maintenance", label: "Bảo trì", icon: <Wrench className="h-4 w-4" />, type: "MAINTENANCE", empty: "Chưa có lần bảo trì nào được ghi." },
                { id: "downtime", label: "Sự cố", icon: <AlertCircle className="h-4 w-4" />, type: "DOWNTIME", empty: "Chưa có sự cố / dừng máy nào được ghi." },
                { id: "logs", label: "Nhật ký máy", icon: <History className="h-4 w-4" />, type: null, empty: "Chưa có nhật ký máy." },
              ] as const
            ).map((t) => {
              const rows = t.type ? logs.filter((l: any) => l.type === t.type) : logs;
              return {
                id: t.id,
                label: t.label,
                icon: t.icon,
                count: rows.length,
                content: (
                  <MiniTable
                    head={["Mã", "Ngày", "Loại", "Thời lượng", "Lệnh SX", "Nguyên nhân / Nội dung", "Xử lý", "Người ghi"]}
                    empty={t.empty}
                    rows={rows.map((l: any) => [
                      <span className="whitespace-nowrap"><EntityLink entityType="machineLog" id={l.id} code={l.logId} /></span>,
                      <span className="tabular-nums whitespace-nowrap">{l.date ? formatDate(l.date) : "—"}</span>,
                      LOG_TYPE[l.type] || l.type,
                      l.durationMinutes ? `${l.durationMinutes} phút` : "—",
                      l.productionOrder ? <span className="whitespace-nowrap"><EntityLink entityType="productionOrder" id={l.productionOrder.id} code={l.productionOrder.orderId} /></span> : "—",
                      <span className="text-slate-700">{[l.issueType, l.reason].filter(Boolean).join(" — ") || "—"}</span>,
                      <span className="text-slate-700">{l.fixAction || "—"}</span>,
                      l.operator?.fullName || "—",
                    ])}
                  />
                ),
              };
            }),
          ]}
        />
      </StandardDetailLayout>

      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xoá máy {d.machineId}?</AlertDialogTitle>
            <AlertDialogDescription>Hành động này không thể hoàn tác.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Huỷ</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-rose-600 hover:bg-rose-700">
              {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Xoá
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
